import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { API_URL, ACH_PRICE } from "../config";
import { useNavigate, useParams } from "react-router-dom";
import Header from "./component/Header";
import PublicFooter from "../components/layout/PublicFooter";
import { StepIndicator } from "../components/ui";
import { customStyles as selectStyles } from "../components/ui/Select";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import PaymentForm from "./component/PaymentGateway";
import Loading from "./component/Loading";
import Swal from "sweetalert2";
import { useAuth } from "./component/AuthContext";
import InputMask from "react-input-mask";

const BillingInformation = () => {
  const { onboardingId } = useParams();
  const [stateOptions, setStateOptions] = useState();
  const [selectedState, setSelectedState] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("ach");
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(null);
  const [rawPhoneNumber, setRawPhoneNumber] = useState("");
  const [transitTokenObject, setTransitTokenObject] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [repeatAccountNumber, setRepeatAccountNumber] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [cardType, setCardType] = useState("");
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [achAgree, setAchAgree] = useState(false);
  const [creditCardAgree, setCreditCardAgree] = useState(false);
  const [termsAgree, setTermsAgree] = useState(false);
  const [smsTermsAgree, setSmsTermsAgree] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [stepsName, setStepsName] = useState({});
  const [accountName, setAccountName] = useState("");
  const [serviceFee, setServiceFee] = useState(Number(ACH_PRICE).toFixed(2));

  const handleAddressSelect = async (address) => {
    setAddress(address);
    try {
      const results = await geocodeByAddress(address);
      if (results && results.length > 0) {
        const selectedAddress = results[0];
        setCity(selectedAddress.address_components.find((comp) => comp.types.includes("locality"))?.long_name || "");
        setPinCode(selectedAddress.address_components.find((comp) => comp.types.includes("postal_code"))?.long_name || "");
        const stateName = selectedAddress.address_components.find((comp) => comp.types.includes("administrative_area_level_1"))?.long_name || "";
        const selectedOption = stateOptions.find((option) => option.label === stateName);
        setSelectedState(selectedOption);
        setSelectedStateId(selectedOption.value);
      }
    } catch (error) {
      console.log("Error fetching address details:", error);
    }
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setSelectedStateId(selectedOption.value);
  };

  useEffect(() => {
    fetch(API_URL + "/states")
      .then((response) => response.json())
      .then((data) => {
        const options = data.map((state) => ({ value: state.id, label: state.name }));
        setStateOptions(options);
      })
      .catch((error) => console.log("Error fetching states:", error));
  }, []);

  useEffect(() => {
    fetch(API_URL + `/onboarding/${onboardingId}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          navigate("/");
        } else {
          setEmail(data.onboarding.email);
          setPhoneNumber(formatPhoneNumber(data.onboarding.phone));
          setRawPhoneNumber(data.onboarding.phone);
          setStepsName(data.steps.stepsName);
          setCurrentStep(data.steps.customPagesCount + 3);
        }
      })
      .catch((error) => { navigate("/"); console.log("Error fetching onboarding data:", error); });
  }, [onboardingId, navigate]);

  const isFormValid = () => {
    let isValid = true;
    const newErrors = { email: "", phoneNumber: "", address: "", city: "", state: "", pinCode: "", password: "", confirmPassword: "", paymentMethod: "" };

    if (!email) { newErrors.email = "Email is required."; isValid = false; }
    else if (!isValidEmail(email)) { newErrors.email = "Invalid email format."; isValid = false; }
    if (!phoneNumber) { newErrors.phoneNumber = "Phone number is required."; isValid = false; }
    else if (!isValidPhoneNumber(phoneNumber)) { newErrors.phoneNumber = "Invalid phone number format."; isValid = false; }
    if (!address) { newErrors.address = "Address is required."; isValid = false; }
    if (!city) { newErrors.city = "City is required."; isValid = false; }
    if (!selectedState) { newErrors.state = "State is required."; isValid = false; }
    if (!pinCode) { newErrors.pinCode = "Zip Code is required."; isValid = false; }
    if (!password) { newErrors.password = "Password is required."; isValid = false; }
    else if (password.length < 6) { newErrors.password = "Password must be at least 6 characters long."; isValid = false; }
    if (!confirmPassword) { newErrors.confirmPassword = "Confirm password is required."; isValid = false; }
    else if (password !== confirmPassword) { newErrors.confirmPassword = "Passwords do not match."; isValid = false; }
    if (!paymentMethod) { newErrors.paymentMethod = "Payment method is required."; isValid = false; }

    if (paymentMethod === "ach") {
      if (!accountNumber) { newErrors.accountNumber = "Account number is required."; isValid = false; }
      if (!repeatAccountNumber) { newErrors.repeatAccountNumber = "Repeat Account number is required."; isValid = false; }
      if (accountNumber !== repeatAccountNumber) { newErrors.repeatAccountNumber = "Account number & Repeat Account number must be same."; isValid = false; }
      if (!accountType) { newErrors.accountType = "Account type is required."; isValid = false; }
      if (!routingNumber) { newErrors.routingNumber = "Routing number is required."; isValid = false; }
      else if (!/^\d{9}$/.test(routingNumber)) { newErrors.routingNumber = "Invalid routing number format. It should be 9 digits."; isValid = false; }
      else if (!bankInfo) { newErrors.routingNumber = "Invalid routing number."; isValid = false; }
    }

    if (!termsAgree || !smsTermsAgree || (paymentMethod === "ach" && !achAgree) || (paymentMethod === "creditCard" && !creditCardAgree)) {
      newErrors.termsAgree = "Please check All terms checkboxes."; isValid = false;
    }

    setErrors(newErrors);
    const combinedErrorMessage = Object.values(newErrors).filter((message) => message).join("\n");
    if (!isValid) {
      Swal.fire({ icon: "error", title: "Please fix the following issues:", html: combinedErrorMessage.replace(/\n/g, "<br>"), showCloseButton: true, showConfirmButton: true });
    }
    return isValid;
  };

  const handleBlur = (field, value) => {
    const newErrors = { ...errors };
    switch (field) {
      case "email": if (!value) newErrors.email = "Email is required."; else if (!isValidEmail(value)) newErrors.email = "Invalid email format."; else newErrors.email = ""; break;
      case "phoneNumber": if (!value) newErrors.phoneNumber = "Phone number is required."; else if (!isValidPhoneNumber(value)) newErrors.phoneNumber = "Invalid phone number format."; else newErrors.phoneNumber = ""; break;
      case "address": newErrors.address = value ? "" : "Address is required."; break;
      case "city": newErrors.city = value ? "" : "City is required."; break;
      case "state": newErrors.state = value ? "" : "State is required."; break;
      case "pinCode": newErrors.pinCode = value ? "" : "Zip Code is required."; break;
      case "password": if (!value) newErrors.password = "Password is required."; else if (value.length < 6) newErrors.password = "Password must be at least 6 characters long."; else newErrors.password = ""; break;
      case "confirmPassword": if (!value) newErrors.confirmPassword = "Confirm password is required."; else if (value !== password) newErrors.confirmPassword = "Passwords do not match."; else newErrors.confirmPassword = ""; break;
      default: break;
    }
    setErrors(newErrors);
  };

  const handleFormSubmit = () => {
    if (!isFormValid()) return;
    setLoading(true);
    fetch(API_URL + "/billing-information/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, phoneNumber, address, city, pinCode, password, paymentMethod, onboardingId, selectedStateId, rawPhoneNumber, transitTokenObject, accountNumber, repeatAccountNumber, accountName, serviceFee, routingNumber, accountType, cardType, achAgree, creditCardAgree, termsAgree, accountType }),
    })
      .then((response) => {
        setLoading(false);
        if (response.status === 422) return response.json().then((data) => { throw new Error(Object.values(data.errors).join("\n")); });
        else if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        login(data);
        Swal.fire({ icon: "success", title: "Success!", text: "Congratulations! Your account has been successfully created.", showConfirmButton: true });
        navigate("/welcome");
      })
      .catch((error) => {
        setLoading(false);
        if (error.response && error.response.data && error.response.data.errors) {
          Swal.fire({ icon: "error", title: "Error submitting the form", html: Object.values(error.response.data.errors).join("<br>"), showCloseButton: true, showConfirmButton: true });
        } else {
          Swal.fire({ icon: "error", title: "Error submitting the form", text: error.message || "An unexpected error occurred", showCloseButton: true, showConfirmButton: true });
        }
      });
  };

  const formatPhoneNumber = (value) => {
    const formattedValue = value.replace(/\D/g, "");
    let formattedPhoneNumber = "";
    if (formattedValue.length > 0) {
      formattedPhoneNumber = "(" + formattedValue.slice(0, 3) + ") ";
      if (formattedValue.length > 3) { formattedPhoneNumber += formattedValue.slice(3, 6) + "-"; if (formattedValue.length > 6) formattedPhoneNumber += formattedValue.slice(6, 10); }
    }
    return formattedPhoneNumber;
  };

  const handlePhoneNumberChange = (e) => {
    const formattedPhoneNumber = e.target.value;
    setPhoneNumber(formattedPhoneNumber);
    setRawPhoneNumber(formattedPhoneNumber.replace(/\D/g, ""));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhoneNumber = (phoneNumber) => /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(phoneNumber);

  const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-400 tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100";
  const errorClasses = "tw-text-xs tw-text-error tw-m-0 tw-mt-1";

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <Header />
      {loading && <Loading text="Verifying Card..." />}

      {/* Hero Banner */}
      <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-800 tw-py-10 md:tw-py-14">
        <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <h1 className="tw-text-4xl tw-text-white tw-m-0">Billing Information</h1>
          <p className="tw-text-lg tw-text-primary-200 tw-mt-2 tw-mb-0">Set up your account and payment method to complete registration.</p>
        </div>
      </div>

      <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {stepsName && Object.keys(stepsName).length > 0 && (
          <StepIndicator steps={Object.values(stepsName)} currentStep={currentStep - 1} />
        )}

        <div className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100 tw-p-6 md:tw-p-8">
          <form className="tw-space-y-7">
            {/* Contact Info */}
            <h2 className="tw-text-2xl tw-text-gray-900 tw-m-0 tw-mb-2">Contact Information</h2>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="emailInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Email</label>
                <input type="email" className={inputClasses} id="emailInput" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => handleBlur("email", email)} />
                {errors.email && <p className={errorClasses}>{errors.email}</p>}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="phoneNumberInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Phone Number</label>
                <InputMask mask="(999) 999-9999" id="phoneNumberInput" name="phoneNumber" value={phoneNumber} onChange={handlePhoneNumberChange} className={inputClasses} placeholder="Enter your phone number" onBlur={() => handleBlur("phoneNumber", phoneNumber)} type="tel" inputMode="numeric" />
                {errors.phoneNumber && <p className={errorClasses}>{errors.phoneNumber}</p>}
              </div>
            </div>

            {/* Address */}
            <h3 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mt-6 tw-mb-2 tw-pt-4 tw-border-t tw-border-gray-200">Address</h3>

            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label htmlFor="addressInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Street Address</label>
              <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleAddressSelect}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                  <div className="tw-relative">
                    <input className={inputClasses} {...getInputProps({ placeholder: "Enter your address" })} />
                    {suggestions.length > 0 && (
                      <div className="tw-absolute tw-z-50 tw-w-full tw-mt-1 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-max-h-60 tw-overflow-auto">
                        {loading && <div className="tw-p-3 tw-text-gray-500">Loading...</div>}
                        {suggestions.map((suggestion, index) => (
                          <div key={index} {...getSuggestionItemProps(suggestion)} className={`tw-p-3 tw-cursor-pointer tw-transition-colors ${suggestion.active ? 'tw-bg-primary-500 tw-text-white' : 'hover:tw-bg-gray-50'}`}>
                            {suggestion.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </PlacesAutocomplete>
              {errors.address && <p className={errorClasses}>{errors.address}</p>}
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="cityInput" className="tw-text-sm tw-font-medium tw-text-gray-700">City</label>
                <input type="text" className={inputClasses} id="cityInput" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} onBlur={() => handleBlur("city", city)} />
                {errors.city && <p className={errorClasses}>{errors.city}</p>}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="stateSelect" className="tw-text-sm tw-font-medium tw-text-gray-700">State</label>
                <Select id="stateSelect" styles={selectStyles} options={stateOptions} value={selectedState} onChange={handleStateChange} onBlur={() => handleBlur("state", selectedState)} />
                {errors.state && <p className={errorClasses}>{errors.state}</p>}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="pinCodeInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Zip Code</label>
                <input type="text" className={inputClasses} id="pinCodeInput" placeholder="Zip Code" value={pinCode} onChange={(e) => setPinCode(e.target.value)} onBlur={() => handleBlur("pinCode", pinCode)} />
                {errors.pinCode && <p className={errorClasses}>{errors.pinCode}</p>}
              </div>
            </div>

            {/* Password */}
            <h3 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mt-6 tw-mb-2 tw-pt-4 tw-border-t tw-border-gray-200">Create Password</h3>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="passwordInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Password</label>
                <input type="password" className={inputClasses} id="passwordInput" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} onBlur={() => handleBlur("password", password)} />
                {errors.password && <p className={errorClasses}>{errors.password}</p>}
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="confirmPasswordInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Confirm Password</label>
                <input type="password" className={inputClasses} id="confirmPasswordInput" placeholder="Confirm your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onBlur={() => handleBlur("confirmPassword", confirmPassword)} />
                {errors.confirmPassword && <p className={errorClasses}>{errors.confirmPassword}</p>}
              </div>
            </div>

            {/* Payment */}
            <h3 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mt-6 tw-mb-2 tw-pt-4 tw-border-t tw-border-gray-200">Payment Method</h3>

            <PaymentForm
              accountType={accountType} setAccountType={setAccountType}
              cardType={cardType} setCardType={setCardType}
              routingNumber={routingNumber} setRoutingNumber={setRoutingNumber}
              accountNumber={accountNumber} setAccountNumber={setAccountNumber}
              setRepeatAccountNumber={setRepeatAccountNumber}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              transitTokenObject={transitTokenObject} setTransitTokenObject={setTransitTokenObject}
              bankInfo={bankInfo} setBankInfo={setBankInfo}
              accountName={accountName} setAccountName={setAccountName}
              address={address} setAddress={setAddress}
              city={city} setCity={setCity}
              isShowTermsAndConditions={true}
              achAgree={achAgree} setAchAgree={setAchAgree}
              creditCardAgree={creditCardAgree} setCreditCardAgree={setCreditCardAgree}
              termsAgree={termsAgree} setTermsAgree={setTermsAgree}
              smsTermsAgree={smsTermsAgree} setSmsTermsAgree={setSmsTermsAgree}
              serviceFee={serviceFee} setServiceFee={setServiceFee}
            />

            {/* Summary */}
            <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
              <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-4 tw-mb-6">
                <h4 className="tw-text-sm tw-font-semibold tw-text-blue-900 tw-m-0 tw-mb-2">What happens next</h4>
                <ul className="tw-m-0 tw-p-0 tw-list-none tw-space-y-1.5">
                  <li className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-blue-800">
                    <svg className="tw-w-4 tw-h-4 tw-text-blue-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    We'll monitor your plates and notify you before each renewal
                  </li>
                  <li className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-blue-800">
                    <svg className="tw-w-4 tw-h-4 tw-text-blue-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    Renewal charges are processed automatically using your payment method
                  </li>
                  <li className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-blue-800">
                    <svg className="tw-w-4 tw-h-4 tw-text-blue-500 tw-mt-0.5 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    New tags are mailed directly to your address
                  </li>
                </ul>
              </div>
            </div>

            {/* Submit */}
            <div>
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={paymentMethod === "creditCard" ? !transitTokenObject : false}
                className="tw-w-full sm:tw-w-auto tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-accent-500 hover:tw-bg-accent-600 tw-text-white tw-px-10 tw-py-3.5 tw-rounded-lg tw-text-lg tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-sm hover:tw-shadow-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Complete Sign Up <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </form>
        </div>

        {/* Security reassurance */}
        <div className="tw-mt-6 tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-xs tw-text-gray-400">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
            <span>256-bit SSL encryption</span>
          </div>
          <span className="tw-text-gray-300">·</span>
          <span>PCI-compliant payment processing</span>
          <span className="tw-text-gray-300">·</span>
          <span>Your data is never shared</span>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default BillingInformation;
